import { Directive, ElementRef, OnDestroy, OnChanges, SimpleChanges, inject, input, output } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Draggable Directive for Angular2
 *
 * Inspiration:
 *   https://github.com/AngularClass/angular2-examples/blob/master/rx-draggable/directives/draggable.ts
 *   http://stackoverflow.com/questions/35662530/how-to-implement-drag-and-drop-in-angular2
 *
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[draggable]'
})
export class DraggableDirective implements OnDestroy, OnChanges {
  readonly dragEventTarget = input<unknown>(undefined);
  readonly dragModel = input<unknown>(undefined);
  readonly dragX = input<boolean>(true);
  readonly dragY = input<boolean>(true);

  readonly dragStart = output<unknown>();
  readonly dragging = output<unknown>();
  readonly dragEnd = output<unknown>();

  element: HTMLElement;
  isDragging: boolean = false;
  subscription: Subscription;

  constructor() {
    const element = inject(ElementRef);

    this.element = element.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dragEventTarget'] && changes['dragEventTarget'].currentValue && (this.dragModel() as Record<string, unknown>)?.['dragging']) {
      this.onMousedown(changes['dragEventTarget'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this._destroySubscription();
  }

  onMouseup(event: MouseEvent): void {
    if (!this.isDragging) {return;}

    this.isDragging = false;
    this.element.classList.remove('dragging');

    if (this.subscription) {
      this._destroySubscription();
      this.dragEnd.emit({
        event,
        element: this.element,
        model: this.dragModel()
      });
    }
  }

  onMousedown(event: MouseEvent): void {
    // we only want to drag the inner header text
    const isDragElm = (event.target as HTMLElement).classList.contains('draggable');

    if (isDragElm && (this.dragX() || this.dragY())) {
      event.preventDefault();
      this.isDragging = true;

      const mouseDownPos = { x: event.clientX, y: event.clientY };

      const mouseup = fromEvent(document, 'mouseup');
      this.subscription = mouseup.subscribe((ev: Event) => this.onMouseup(ev as MouseEvent));

      const mouseMoveSub = fromEvent(document, 'mousemove')
        .pipe(takeUntil(mouseup))
        .subscribe((ev: Event) => this.move(ev as MouseEvent, mouseDownPos));

      this.subscription.add(mouseMoveSub);

      this.dragStart.emit({
        event,
        element: this.element,
        model: this.dragModel()
      });
    }
  }

  move(event: MouseEvent, mouseDownPos: { x: number; y: number }): void {
    if (!this.isDragging) {return;}

    const x = event.clientX - mouseDownPos.x;
    const y = event.clientY - mouseDownPos.y;

    if (this.dragX()) {this.element.style.left = `${x}px`;}
    if (this.dragY()) {this.element.style.top = `${y}px`;}

    this.element.classList.add('dragging');

    this.dragging.emit({
      event,
      element: this.element,
      model: this.dragModel()
    });
  }

  private _destroySubscription(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
